"use client";
import { useDispatch } from "react-redux";
import React, { useRef, useState } from "react";
// import { LockClosedIcon } from "@heroicons/react/24/solid";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { parseResumeFromPdf } from "lib/parse-resume-from-pdf";
import {
  getHasUsedAppBefore,
  saveStateToLocalStorage,
} from "lib/redux/local-storage";
import {
  type ShowForm,
  initialSettings,
  setSettings,
  toggleImportComponent,
} from "lib/redux/settingsSlice";
import {
  changeDisplayName,
  changePublishedStatus,
  changeStatusId,
  changeTemplateId,
  setResume,
  initialResumeState,
  selectResumeId,
} from "lib/redux/resumeSlice";
import { initialUIState } from "lib/redux/uiSlice";
// import addPdfSrc from "public/assets/add-pdf.svg";
// import AddPdfSrc from "public/assets/add-pdf.svg";
import Image from "next/image";
import { cx } from "lib/cx";
import { deepClone } from "lib/deep-clone";
import { convertToHtmlList } from "lib/utils/convertToHtmlList";
import { readRawPdf } from "lib/parse-resume-from-pdf/read-raw-pdf";
import { parseResumeUsingAi } from "api/parsingApi";
import { useAppSelector } from "lib/redux/hooks";
import { setOpenMobileResumeOverlay } from "lib/redux/uiSlice";
import { pdfToMarkdown } from "lib/parse-resume-from-pdf/pdf2md";

export const defaultFileState = {
  name: "",
  size: 0,
  fileUrl: "",
  arrayBuffer: new ArrayBuffer(0),
};

export const ResumeDropzone = ({
  onFileUrlChange,
  className,
  playgroundView = false,
  setHasAddedResume,
}: {
  onFileUrlChange: (fileUrl: string) => void;
  className?: string;
  playgroundView?: boolean;
  setHasAddedResume: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const [file, setFile] = useState(defaultFileState);
  const [isHoveredOnDropzone, setIsHoveredOnDropzone] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasNonPdfFile, setHasNonPdfFile] = useState(false);
   const [importError, setImportError] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const dispatch = useDispatch();
  const resumeId = useAppSelector(selectResumeId);

  const hasFile = Boolean(file.name);

  const setNewFile = async (newFile: File) => {
    if (file.fileUrl) {
      URL.revokeObjectURL(file.fileUrl);
    }

    const { name, size } = newFile;
    const fileUrl = URL.createObjectURL(newFile);
    const arrayBuffer = await newFile.arrayBuffer();
    setFile({ name, size, fileUrl, arrayBuffer });
    onFileUrlChange(fileUrl);
  };

  const onDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const newFile = event.dataTransfer.files[0];
    if (newFile.name.endsWith(".pdf")) {
      setHasNonPdfFile(false);
      setNewFile(newFile);
    } else {
      setHasNonPdfFile(true);
    }
    setIsHoveredOnDropzone(false);
  };

  const onInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    const newFile = files[0];
    setNewFile(newFile);
  };

  const onRemove = () => {
    setFile(defaultFileState);
    onFileUrlChange("");
  };

  const normalizeUrl = (u: string) => {
    if (!u) return u;
    let url = u.trim();
    if (url.startsWith("www.")) url = "https://" + url;
    // strip mailto/tel prefixes when storing url fields
    return url;
  };

  const isLikelyUrl = (s?: string) => {
    if (!s) return false;
    const str = s.trim();
    // quick try using URL constructor
    try {
      // accept strings with protocol
      new URL(str);
      return true;
    } catch (e) {
      try {
        // try adding https
        new URL("https://" + str);
        return true;
      } catch (e2) {
        return false;
      }
    }
  };

  const dedupeByUrl = (links: { url: string; text?: string }[]) => {
    const map = new Map<string, { url: string; text?: string }>();
    for (const l of links) {
      const key = (l.url || "").toString();
      if (!key) continue;
      if (!map.has(key)) map.set(key, l);
    }
    return Array.from(map.values());
  };

  const resumeParser = async (
    rawPdfData: string | { text: string; links: any[] }
  ) => {
    setIsLoading(true);
    try {
      const extractedLinks =
        typeof rawPdfData === "string"
          ? []
          : (rawPdfData.links || []).map((l: any) => ({
              url: l.url,
              text: l.text,
            }));
      // console.log("Extracted Links from PDF: ", extractedLinks);

      const mergedLinks = [] as { url: string; text?: string }[];
      if (extractedLinks && extractedLinks.length) {
        for (const lRaw of extractedLinks) {
          const l = lRaw as any;
          const candidateUrl = l.url || l.href || null;
          if (candidateUrl && isLikelyUrl(candidateUrl)) {
            mergedLinks.push({ url: normalizeUrl(candidateUrl), text: l.text });
          }
        }
      }
      const finalLinks = dedupeByUrl(mergedLinks).filter(
        (l) => l.url && l.url.length
      );

      // prepare markdown variable; we'll fill it after deduping links so we can append them
      let markdown = await pdfToMarkdown(Buffer.from(file.arrayBuffer));
      // console.log("Extracted Links from PDF: ", extractedLinks);
      // If markdown is empty or only whitespace, show an import error and abort
            if (!markdown || !markdown.trim()) {
              setIsLoading(false);
              setImportError("This Resume cannot be imported. Try a different file.");
              // clear selected file so user can try a different file
              try {
                if (file.fileUrl) URL.revokeObjectURL(file.fileUrl);
              } catch (e) {
                /* ignore */
              }
              setFile(defaultFileState);
              onFileUrlChange("");
              return null;
            }
      if (extractedLinks && extractedLinks.length) {
        const linksBlock = extractedLinks
          .map((l) => `url: ${l.url} text: ${l.text || ""}`)
          .join("\n");
        markdown = `${markdown}\n\n---\n\nLinks:\n${linksBlock}`;
      }
      // console.log("Markdown Resume: ", markdown);
      const parsedResume = await parseResumeUsingAi(markdown);
      // console.log("Parsed Resume from AI: ", parsedResume);

      const profileWithLinks = {
        ...(parsedResume.profile || initialResumeState.profile),
      } as any;
      
      const linkedin = finalLinks.find((l) => /linkedin\.com/i.test(l.url));
      const github = finalLinks.find((l) => /github\.com/i.test(l.url));
      if (linkedin) profileWithLinks.url1 = linkedin.url;
      if (github) profileWithLinks.url2 = github.url;

      

      const updateParsedResume = {
        id: parsedResume.id || initialResumeState.id,
        profile: profileWithLinks || initialResumeState.profile,
        workExperiences:
          parsedResume.workExperiences || initialResumeState.workExperiences,
        educations: parsedResume.educations || initialResumeState.educations,
        projects: parsedResume.projects || initialResumeState.projects,
        skills: parsedResume.skills || initialResumeState.skills,
        certifications:
          parsedResume.certifications || initialResumeState.certifications,
        languages: parsedResume.languages || initialResumeState.languages,
        awards: parsedResume.awards || initialResumeState.awards,
        custom: parsedResume.custom || initialResumeState.custom,
        displayName: initialResumeState.displayName,
        createdOn: initialResumeState.createdOn,
        modifiedOn: initialResumeState.modifiedOn,
        templateId: initialResumeState.templateId,
        published: initialResumeState.published,
        statusId: initialResumeState.statusId,
        themeColor: initialResumeState.themeColor,
        fontSize: initialResumeState.fontSize,
        fontFamily: initialResumeState.fontFamily,
        documentSize: initialResumeState.documentSize,
        formToShow: initialResumeState.formToShow,
        formToHeading: initialResumeState.formToHeading,
        formsOrder: initialResumeState.formsOrder,
        leftRightFormOrder: initialResumeState.leftRightFormOrder,
        profileStatus: initialResumeState.profileStatus,
      };
      setHasAddedResume(true);
      setIsLoading(false);
      return updateParsedResume;
    } catch (error) {
      const fallbackResume = await parseResumeFromPdf(file.fileUrl);
      setIsLoading(false);
      return fallbackResume;
    }
  };

  const onImportClick = async () => {
    try {
      const ui = deepClone(initialUIState);
      const settings = deepClone(initialSettings);
      const rawPdfData = await readRawPdf(file.fileUrl);
      // console.log("Raw PDF Data: ", rawPdfData);
      const resume = await resumeParser(rawPdfData);
      if (!resume) {
        // resumeParser sets importError when markdown extraction fails
        return;
      }
      if (resumeId) {
        resume.id = resumeId;
      }

      // Set formToShow settings based on uploaded resume if users have used the app before
      if (getHasUsedAppBefore()) {
        const sections = Object.keys(settings.formToShow) as ShowForm[];
        const sectionToFormToShow: Record<ShowForm, boolean> = {
          workExperiences: resume.workExperiences?.length > 0,
          educations: resume.educations?.length > 0,
          projects: resume.projects?.length > 0,
          skills: resume.skills?.descriptions?.length > 0,
          certifications: resume.certifications?.length > 0,
          languages: resume.languages?.length > 0,
          awards: resume.awards?.length > 0,
          custom: resume.custom?.descriptions?.length > 0,
        };
        for (const section of sections) {
          settings.formToShow[section] = sectionToFormToShow[section];
        }
      }

      const { projects, workExperiences, skills } = resume;

      projects?.forEach((project: any) => {
        project.descriptions = [convertToHtmlList(project.descriptions)];
      });
      workExperiences?.forEach((workExperience: any) => {
        workExperience.descriptions = [
          convertToHtmlList(workExperience.descriptions),
        ];
      });

      if (skills?.descriptions && skills.descriptions.length > 0) {
        skills.descriptions = [convertToHtmlList(skills.descriptions)];
      }

      dispatch(setOpenMobileResumeOverlay(true));
      dispatch(toggleImportComponent());
      dispatch(setResume(resume));
      dispatch(changeDisplayName(""));
      dispatch(changeTemplateId(1));
      dispatch(changePublishedStatus(false));
      dispatch(changeStatusId(1));
      dispatch(setSettings(settings));
      saveStateToLocalStorage({ resume, settings, ui });
    } catch (e) {
      console.log("Error in parsing resume....", e);
    }
  };

  return (
    <div
      className={cx(
        "flex justify-center rounded-md px-3 lg:px-6 ",
        isHoveredOnDropzone && "border-sky-400",
        playgroundView ? "pb-6 pt-4" : "py-6 lg:py-12"
      )}
      onDragOver={(event) => {
        event.preventDefault();
        setIsHoveredOnDropzone(true);
      }}
      onDragLeave={() => setIsHoveredOnDropzone(false)}
      onDrop={onDrop}
    >
      <div
        className={cx(
          "text-center",
          playgroundView ? "space-y-2" : "space-y-2"
        )}
        onClick={() => {
          if (!hasFile) {
            inputRef.current?.click();
          }
        }}
      >
        <input
          ref={inputRef}
          type="file"
          className="sr-only"
          accept=".pdf"
          onChange={onInputChange}
        />
        {!playgroundView && (
          <Image
            src="/icons/uploadIcon.svg"
            width={70}
            height={70}
            alt="upload"
            className="mx-auto h-8 w-8 xs:h-10 xs:w-10 sm:h-12 sm:w-12 md:h-14 md:w-14 lg:h-16 lg:w-16"
          />
          // <Image
          //   src={addPdfSrc}
          //   className="mx-auto h-14 w-14"
          //   alt="Add pdf"
          //   aria-hidden="true"
          //   priority
          // />
        )}
        {!hasFile ? (
          <>
            <p
              className={cx(
                "pt-1 text-gray-700 lg:pt-3",
                !playgroundView &&
                  "text-[8px] font-semibold xs:text-[10px] sm:text-xs md:text-sm lg:text-base xl:text-lg"
              )}
            >
              Drag and drop files or
              <label
                className={cx(
                  "within-outline-theme-purple ml-1 cursor-pointer text-[8px] font-semibold underline shadow-sm xs:text-[10px] sm:text-xs md:text-sm lg:text-base",
                  playgroundView ? "border" : ""
                )}
              >
                Browse
              </label>
            </p>
            <p className="flex w-full items-center justify-center text-center text-[6px] text-gray-500 xs:text-[8px] sm:text-[10px] md:text-xs lg:text-sm">
              Supported formats: PDF
            </p>
            {importError && (
                <p className="mt-3 text-sm text-red-500" role="alert">
                  {importError}
                </p>
              )}
          </>
        ) : (
          <div className="flex items-center justify-center">
            <div className="pl-7 text-[7px] font-semibold text-gray-900 md:text-sm lg:text-lg">
              {file.name.slice(0, 5)}... - {getFileSizeString(file.size)}
            </div>
            <button
              type="button"
              className="outline-theme-blue rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-500"
              title="Remove file"
              onClick={onRemove}
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        )}
        <div className="pt-2 lg:pt-4">
          {!hasFile ? (
            <></>
          ) : (
            <>
              {!playgroundView && (
                <button
                  type="button"
                  className={` ${
                    !isLoading ? "btn-gradient" : ""
                  } inline-block rounded-full px-3 py-1 text-[6px] font-semibold xs:px-4 xs:py-1.5 xs:text-[10px] sm:px-5 sm:py-1.5 sm:text-xs md:px-6 md:py-2 md:text-sm lg:text-base`}
                  onClick={onImportClick}
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center">
                      <div className="xs:border-3 h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 xs:h-5 xs:w-5 sm:h-6 sm:w-6 sm:border-4 md:h-8 md:w-8"></div>
                    </div>
                  ) : (
                    <span>
                      {" "}
                      Import and Continue <span aria-hidden="true">→</span>
                    </span>
                  )}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const getFileSizeString = (fileSizeB: number) => {
  const fileSizeKB = fileSizeB / 1024;
  const fileSizeMB = fileSizeKB / 1024;
  if (fileSizeKB < 1000) {
    return fileSizeKB.toPrecision(3) + " KB";
  } else {
    return fileSizeMB.toPrecision(3) + " MB";
  }
};
